import VideoService from "@/api/services/videoService";
import { useUserStore } from "@/store/user";
import { defineStore } from "pinia";
import { Video } from "@/interfaces/Video/Video.ts";
import { VideoList } from "@/interfaces/Video/VideoList.ts";
import { useFeatureStore } from "@/store/feature.ts";

interface VideoState {
  activeVideo: number | string | null;
  unlocked: number[];
  unlockedBusy: boolean;
  videos: Record<number | string, Video>;
  list: VideoList;
  listBusy: boolean;
  lastPortion: number | null;
}

export const useVideoStore = defineStore("video", {
  state: (): VideoState => ({
    activeVideo: null,
    unlocked: [],
    unlockedBusy: false,
    videos: {},
    list: {} as VideoList,
    listBusy: false,
    lastPortion: null,
  }),

  getters: {
    getActiveVideo: (state: VideoState) => state.activeVideo,
    getUnlocked: (state: VideoState) => state.unlocked,
    isPlayable: (state: VideoState) => (video: Video) => {
      const hasTrailerFeature = useFeatureStore().hasTrailerFeature;

      return (
        state.unlocked.includes(video.id) ||
        useUserStore().isPremium ||
        (hasTrailerFeature && video?.tags && video.tags.filter((tag) => tag.name_en === "Trailer").length > 0)
      );
    },
    getVideo: (state: VideoState) => (id: number | string) => state.videos[id],
    getList: (state: VideoState) => state.list,
    isListBusy: (state: VideoState) => state.listBusy,
    getLastPortion: (state: VideoState) => state.lastPortion,
  },

  actions: {
    setActiveVideo(videoId: number | string | null) {
      this.activeVideo = videoId;
    },
    async fetchUnlocked() {
      if (this.unlockedBusy) {
        return;
      }

      try {
        this.unlockedBusy = true;
        this.unlocked = await VideoService.getUnlockedVideos();
      } catch {
        this.unlocked = [];
      }

      this.unlockedBusy = false;
    },
    async fetchVideo(id: number) {
      const response = await VideoService.getVideo(id);
      this.setVideo(response);
    },
    async fetchList(params: { page: number; order: string; tags: Array<number>; term: string; search_type: string | null }) {
      if (this.listBusy) {
        return;
      }

      if (JSON.stringify(params) === JSON.stringify(this.list.params)) {
        return;
      }

      this.listBusy = true;
      const response = await VideoService.getList(params);
      response.params = params;
      this.list = response;

      this.listBusy = false;

      response.videos.forEach((video) => {
        this.setVideo(video);
      });
    },
    setVideo(video: Video) {
      if (this.videos[video.id]) {
        return;
      }

      const newVideo: Record<number, Video> = {};
      newVideo[video.id] = video;

      this.videos = {
        ...this.videos,
        ...newVideo,
      };
    },
    setLastPortion(value: number) {
      this.lastPortion = value;
    },

    increaseCommentCount(id: number) {
      const video = this.videos[id] ?? null;

      if (video === null || video.total_comments === undefined) {
        return;
      }

      video.total_comments++;
      this.videos[id] = video;
    },
  },
});
